import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { StatusChip, TABLE_PAGINATION_SX, TABLE_PAPER_SX, TABLE_ROWS_PER_PAGE_OPTIONS, TABLE_TOOLBAR_SX, ROW_NUMBER_BADGE_SX } from "../../../components/ui";
import {
  CASE_GROUP_DIVIDER,
  CASE_GROUP_HEAD_CELL_IDS,
  COLUMN_COUNT,
  getBodyCellSx,
  getHeadCellSx,
  getRowCellSx,
  HEAD_CELL_BY_ID,
  HEAD_CELLS,
  MATTER_GROUP_CELL_SX,
  OVERDUE_ROW_HOVER_BG,
  ROW_NUMBER_WIDTH_PCT,
  TABLE_HEADER_CELL_SX,
  TASK_HEAD_CELL_IDS,
} from "./constants";
import type { RowWithSpans } from "./types";
type NegotiationsTableProps = {
  loading: boolean;
  hasLoadedData: boolean;
  uniqueCaseCount: number;
  taskCount: number;
  rows: RowWithSpans[];
  caseGroupCount: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
};

function isOverdue(dueDate?: string | null) {
  return !!dueDate && dayjs(dueDate).isBefore(dayjs(), "day");
}

function DateCell({ value, withTime = false }: { value?: string | null; withTime?: boolean }) {
  if (!value) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  return (
    <Typography
      variant="body2"
      sx={{ fontSize: "0.8125rem", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
    >
      {dayjs(value).format(withTime ? "MM/DD/YYYY h:mm A" : "MM/DD/YYYY")}
    </Typography>
  );
}

function DueDateCell({ dueDate, overdue }: { dueDate?: string | null; overdue: boolean }) {
  if (!dueDate) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.8125rem",
          fontWeight: overdue ? 700 : 500,
          color: overdue ? "error.dark" : "text.primary",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {dayjs(dueDate).format("MM/DD/YYYY")}
      </Typography>
      {overdue && (
        <StatusChip
          size="small"
          label="Overdue"
          tone="error"
          sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
        />
      )}
    </Stack>
  );
}

export function NegotiationsTable({
  loading,
  hasLoadedData,
  uniqueCaseCount,
  taskCount,
  rows,
  caseGroupCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}: NegotiationsTableProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        ...TABLE_PAPER_SX,
        flex: 1,
        minHeight: 0,
      }}
    >
      {hasLoadedData && (
        <>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
            spacing={1}
            sx={TABLE_TOOLBAR_SX}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <StatusChip
                size="small"
                label={`${uniqueCaseCount} case${uniqueCaseCount === 1 ? "" : "s"}`}
                sx={{ fontWeight: 600, bgcolor: "background.paper" }}
              />
              <StatusChip
                size="small"
                label={`${taskCount} task${taskCount === 1 ? "" : "s"}`}
                sx={{ fontWeight: 600, bgcolor: "background.paper" }}
              />
            </Stack>
          </Stack>
          <Divider />
        </>
      )}

      <TableContainer sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: "fixed",
            width: "100%",
            "& .MuiTableCell-stickyHeader": {
              boxShadow: "0 1px 0 rgba(15, 23, 42, 0.12)",
            },
          }}
        >
          <colgroup>
            <col style={{ width: `${ROW_NUMBER_WIDTH_PCT}%` }} />
            {HEAD_CELLS.map((headCell) => (
              <col key={headCell.id} style={{ width: `${headCell.widthPct}%` }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...TABLE_HEADER_CELL_SX, width: `${ROW_NUMBER_WIDTH_PCT}%` }}>
                #
              </TableCell>
              {HEAD_CELLS.map((headCell) => (
                <TableCell key={headCell.id} sx={getHeadCellSx(headCell)}>
                  {headCell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 10 }}>
                  <Stack spacing={1.5} alignItems="center">
                    <CircularProgress size={36} />
                    <Typography variant="body2" color="text.secondary">
                      Loading negotiations…
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 8 }}>
                  <Stack spacing={0.75} alignItems="center">
                    <Typography variant="subtitle2" color="text.primary">
                      {hasLoadedData ? "No matching tasks" : "No report loaded"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {hasLoadedData
                        ? "Try adjusting or clearing filters."
                        : "Click Load Report to view cases in negotiation."}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const overdue = isOverdue(row.dueDate);
                const caseDividerSx = row.isLastInCaseGroup ? CASE_GROUP_DIVIDER : {};
                const taskCellSx = { ...getRowCellSx(overdue), ...caseDividerSx };
                const groupCellSx = MATTER_GROUP_CELL_SX;

                return (
                  <TableRow
                    key={`${row.matterId}-${row.taskId ?? 0}`}
                    hover
                    sx={{
                      "&.MuiTableRow-hover:hover": { bgcolor: "transparent" },
                      "&:hover .neg-task-cell": {
                        bgcolor: overdue ? OVERDUE_ROW_HOVER_BG : "rgba(241, 245, 249, 0.9)",
                      },
                    }}
                  >
                    {!row.skipCaseColumns ? (
                      <>
                        <TableCell rowSpan={row.groupRowSpan} sx={groupCellSx}>
                          <Box sx={ROW_NUMBER_BADGE_SX}>
                            {row.rowNumber}
                          </Box>
                        </TableCell>
                        {CASE_GROUP_HEAD_CELL_IDS.map((columnId) => (
                          <TableCell
                            key={columnId}
                            rowSpan={row.groupRowSpan}
                            sx={{
                              ...groupCellSx,
                              ...getBodyCellSx(HEAD_CELL_BY_ID[columnId], {
                                bgcolor: groupCellSx.bgcolor,
                                ...(columnId === "caseName"
                                  ? { fontWeight: 600, color: "text.primary" }
                                  : {}),
                              }),
                            }}
                          >
                            {columnId === "caseName" && row.caseName}
                            {columnId === "raa" && (row.raa || "—")}
                            {columnId === "negotiationStartedAt" && (
                              <DateCell value={row.negotiationStartedAt} />
                            )}
                          </TableCell>
                        ))}
                      </>
                    ) : null}
                    {TASK_HEAD_CELL_IDS.map((columnId) => {
                      if (columnId === "taskName") {
                        return (
                          <TableCell
                            key={columnId}
                            className="neg-task-cell"
                            sx={{
                              ...taskCellSx,
                              ...getBodyCellSx(HEAD_CELL_BY_ID.taskName, { fontWeight: 500 }),
                            }}
                          >
                            {row.taskName}
                          </TableCell>
                        );
                      }

                      if (columnId === "taskedAt") {
                        return (
                          <TableCell
                            key={columnId}
                            className="neg-task-cell"
                            sx={{ ...taskCellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.taskedAt) }}
                          >
                            <DateCell value={row.taskedAt} withTime />
                          </TableCell>
                        );
                      }

                      if (columnId === "dueDate") {
                        return (
                          <TableCell
                            key={columnId}
                            className="neg-task-cell"
                            sx={{
                              ...taskCellSx,
                              ...getBodyCellSx(HEAD_CELL_BY_ID.dueDate, {
                                whiteSpace: "nowrap",
                              }),
                            }}
                          >
                            <DueDateCell dueDate={row.dueDate} overdue={overdue} />
                          </TableCell>
                        );
                      }

                      if (columnId === "taskAssignee") {
                        return (
                          <TableCell
                            key={columnId}
                            className="neg-task-cell"
                            sx={{ ...taskCellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.taskAssignee) }}
                          >
                            {row.taskAssignee || "—"}
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell
                          key={columnId}
                          className="neg-task-cell"
                          sx={{ ...taskCellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.status) }}
                        >
                          {row.status || "—"}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider />
      <TablePagination
        rowsPerPageOptions={[...TABLE_ROWS_PER_PAGE_OPTIONS]}
        component="div"
        count={caseGroupCount}
        rowsPerPage={rowsPerPage}
        page={page}
        labelRowsPerPage="Cases per page"
        onPageChange={(_, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(e) => {
          onRowsPerPageChange(parseInt(e.target.value, 10));
        }}
        sx={TABLE_PAGINATION_SX}
      />
    </Paper>
  );
}
